import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDashboard,
  updateDashboard,
  deleteDashboard,
  updateCardPositions,
  executeQuery,
} from '../api/analyticsApi';
import type { SavedCard, Dashboard, QueryResult } from '../types/analytics.types';

function getStaleTimeForFrequency(
  frequency: SavedCard['refreshFrequency']
): number {
  switch (frequency) {
    case 'realtime':
      return 0;
    case 'hourly':
      return 1000 * 60 * 60;
    case 'daily':
      return 1000 * 60 * 60 * 24;
    case 'weekly':
      return 1000 * 60 * 60 * 24 * 7;
    default:
      return 1000 * 60 * 60;
  }
}

function getRefetchIntervalForFrequency(
  frequency: SavedCard['refreshFrequency']
): number | false {
  switch (frequency) {
    case 'realtime':
      return 30000;
    default:
      return false;
  }
}

export function useDashboard(dashboardId: string | null) {
  return useQuery({
    queryKey: ['dashboard', dashboardId],
    queryFn: () => (dashboardId ? getDashboard(dashboardId) : null),
    enabled: !!dashboardId,
    staleTime: 30000,
  });
}

export function useCardData(card: SavedCard) {
  return useQuery<QueryResult>({
    queryKey: ['cardData', card.id, card.query],
    queryFn: async () => {
      const response = await executeQuery(card.query);
      return response.result;
    },
    staleTime: getStaleTimeForFrequency(card.refreshFrequency),
    refetchInterval: getRefetchIntervalForFrequency(card.refreshFrequency),
  });
}

export function useUpdateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<Dashboard, 'name' | 'isDefault'>>;
    }) => updateDashboard(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDashboard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

export function useUpdateCardPositions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardId,
      positions,
    }: {
      dashboardId: string;
      positions: Array<{
        cardId: string;
        position: { x: number; y: number; w: number; h: number };
      }>;
    }) => updateCardPositions(dashboardId, positions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['dashboard', variables.dashboardId],
      });
    },
  });
}
